export type GroceryItem = {
  id: string;
  name: string;
  imageUrl: string;
};

function titleCase(input: string) {
  return input.replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

function prettyNameFromId(id: string) {
  // Remove trailing numeric variants like "broccoli-2"
  const normalized = id.replace(/-\d+$/, "").replace(/-/g, " ");
  return titleCase(normalized)
    .replace(/\bAnd\b/g, "&")
    .replace(/\bS\s/g, "s ")
    .trim();
}

// Static list of all grocery item IDs (from public/grocery-items folder)
const GROCERY_ITEM_IDS = [
  "all-purpose-flour",
  "almond-milk",
  "almonds",
  "aloe-vera-drink",
  "apple-cider-vinegar",
  "artichoke",
  "bacon-strip",
  "bananas",
  "bao-buns",
  "bell-peppers",
  "black-coffee",
  "black-vinegar",
  "blueberries",
  "bread-rolls",
  "bread",
  "broccoli",
  "brown-rice",
  "brownies",
  "butter",
  "button-mushrooms",
  "canned-beans",
  "canned-corn",
  "canned-octopus",
  "canned-spam",
  "canned-tuna",
  "carrot-soup",
  "carrots",
  "cat-food",
  "cauliflower",
  "celery",
  "chanterelle-mushrooms",
  "cheese",
  "cheeseburgers",
  "chicken",
  "chili-crisp",
  "chili-peppers",
  "chocolate-cake",
  "cooked-shrimp",
  "cookies",
  "cooking-oil",
  "couscous",
  "crackers",
  "cremini-mushrooms",
  "cupcakes",
  "curry-powder",
  "diced-tomatoes",
  "dinner-rolls",
  "dog-food",
  "donuts",
  "drinkable-yogurt",
  "egg-tarts",
  "eggs",
  "elderflower-soda",
  "english-muffins",
  "enoki-mushrooms",
  "fermented-chili-bean-paste",
  "french-fries",
  "fruit-bowl",
  "garlic",
  "gochujang",
  "granola-bars",
  "granola",
  "grapes",
  "greek-yogurt",
  "halloumi-cheese",
  "heavy-cream",
  "herb-bundle",
  "herbal-tea",
  "hot-dogs",
  "hot-sauce",
  "ice-cream",
  "iced-tea",
  "instant-ramen",
  "jello",
  "kale",
  "kefir",
  "kimchi",
  "kombucha",
  "latte",
  "lemons",
  "lettuce",
  "mac-and-cheese",
  "mayo",
  "microgreens",
  "mille-cake",
  "mixed-frozen-vegetables",
  "mochi-ice-cream",
  "naan",
  "nitro-cold-brew",
  "oat-milk",
  "oatmeal",
  "olive-oil",
  "orange-juice",
  "oranges",
  "oyster-mushrooms",
  "pancake-mix",
  "pasta-variety-pack",
  "peanut-butter",
  "pickled-garlic",
  "pizza",
  "plantains",
  "pork-chops",
  "portobello-mushrooms",
  "potato-chips",
  "potatoes",
  "pretzels",
  "purple-yam",
  "rainbow-carrots",
  "red-and-golden-apples",
  "rice-cakes",
  "rice-flour",
  "rotisserie-chicken",
  "salad",
  "salmon-fillets",
  "salt-and-pepper",
  "sardines-in-mustard-sauce",
  "sauerkraut",
  "sesame-seed-buns",
  "sesame-tahini",
  "sheep-s-milk-yogurt",
  "shiitake-mushrooms",
  "skyr",
  "sliced-turkey",
  "soba-noodles",
  "soda-can",
  "soup",
  "sourdough",
  "soy-sauce",
  "spinach",
  "spring-rolls",
  "strawberries",
  "string-cheese",
  "sweet-potatoes",
  "swiss-cheese",
  "taro-buns",
  "tempeh",
  "tofu",
  "tomato-sauce",
  "tortillas",
  "udon-noodles",
  "umami-seasoning",
  "vinegar",
  "waffles",
  "wasabi-peas",
  "whipped-cream",
  "white-bread",
  "white-rice",
  "whole-milk",
  "yellow-onions",
  "yogurt",
];

export function loadGroceryItems(): GroceryItem[] {
  const items = GROCERY_ITEM_IDS.map((id) => ({
    id,
    name: prettyNameFromId(id),
    imageUrl: `${process.env.PUBLIC_URL || ""}/grocery-items/${id}.png`,
  }));

  // Sort stable so UI doesn't shuffle on refresh unless user chooses "Random"
  items.sort((a, b) => a.name.localeCompare(b.name));
  return items;
}

// Static list of personality card types (from public/personality-cards folder)
export const PERSONALITY_TYPES = [
  "aesthetic-nourisher",
  "balanced-overthinker",
  "caffeinated-visionary",
  "chaos-chef",
  "chill-gut-guru",
  "cozy-carb-collector",
  "culinary-maximalist",
  "cultural-collector",
  "farmers-market-poet",
  "fridge-curator",
  "green-alchemist",
  "indulgent-existenilist",
  "label-philosopher",
  "minimalist-forager",
  "snack-theorist",
  "time-traveler",
];

export function getPersonalityCardUrl(personalityType: string): string | null {
  if (!PERSONALITY_TYPES.includes(personalityType)) {
    return null;
  }
  return `${process.env.PUBLIC_URL || ""}/personality-cards/${personalityType}.jpeg`;
}
